import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {useObservable} from '@ngneat/react-rxjs';
import {finalize, switchMap} from 'rxjs';

import {supplierDetailsQuery, supplierDetailsService} from '@store/suppliers/details';

import OptionWithAlertComponent from '@components/moreOptions/OptionWithAlert.component';
import HeadOfficeForm from '@components/unitDetails/HeadOffice.form';
import {ManageContact, ManageContactType} from '@store/common/organisation.model';
import ManageContactDrawer from '@components/unitDetails/ManageContact.drawer';
import {GlobalStatus} from '@store/common/globalStatus.enum';
import {suppliersUnitsService} from '@store/suppliers/units';
 
const HeadOfficeTab = () => {
  const {t} = useTranslation();
  const {supplierId} = useParams();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();

  const [loading, setLoading] = useState(false);

  const [openContact, setOpenContact] = useState<ManageContact | boolean>(false);

  const [{headOffice}] = useObservable(supplierDetailsQuery.supplierDetails$);

  useEffect(() => {
    if (supplierId) {
      supplierDetailsService.getSupplierById(supplierId).subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [enqueueSnackbar, supplierId]);

  const handleClose = () => {
    navigate('../..', {replace: true});
  };

  const isArchived = supplierDetailsQuery.isArchived;

  const handleEdit = (supplier) => {
    if (!supplierId) return;
    setLoading(true);
    supplierDetailsService.updateSupplier(supplierId, supplier).pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: () => {
          enqueueSnackbar(t('suppliers.success.edit'), {variant: 'success'});
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  const handleDeleteContact = (type: ManageContactType, organisationId, contactId, successCallback) => {
    if (!organisationId || !contactId) return;
    setLoading(true);
    switch (type) {
      case ManageContactType.ORGANISATION:
        supplierDetailsService.deleteSupplierContact(organisationId, contactId).pipe(
          finalize(() => setLoading(false)),
          switchMap(() => supplierDetailsService.getSupplierById(supplierId ?? organisationId))
        )
          .subscribe({
            next: () => {
              enqueueSnackbar(t('delete.success.contact'), {variant: 'success'});
              successCallback();
            },
            error: (err) => enqueueSnackbar(err.text, err.options),
          });
        break;
      case ManageContactType.ORGANISATION_UNIT:
        suppliersUnitsService.deleteSuppliersUnitContact(organisationId, contactId).pipe(
          finalize(() => setLoading(false)),
          switchMap(() => supplierDetailsService.getSupplierById(supplierId ?? organisationId))
        )
          .subscribe({
            next: () => {
              enqueueSnackbar(t('delete.success.contact'), {variant: 'success'});
              successCallback();
            },
            error: (err) => enqueueSnackbar(err.text, err.options),
          });
        break;
      default:
        supplierDetailsService.deleteSupplierContact(organisationId, contactId).pipe(
          finalize(() => setLoading(false)),
          switchMap(() => supplierDetailsService.getSupplierById(supplierId ?? organisationId))
        )
          .subscribe({
            next: () => {
              enqueueSnackbar(t('delete.success.contact'), {variant: 'success'});
              successCallback();
            },
            error: (err) => enqueueSnackbar(err.text, err.options),
          });
    }
  };

  return (
    <>
      <HeadOfficeForm
        organisation={headOffice}
        isSupplier
        handleSave={handleEdit}
        handleManageContact={setOpenContact}
        handleDeleteContact={handleDeleteContact}
        loading={loading}
        moreOptions={[
          <OptionWithAlertComponent
            key="manage status"
            alertTitle={t(`${isArchived ? 'unarchive' : 'archive'}.title`)}
            alertDescription={t(`${isArchived ? 'unarchive' : 'archive'}.description.supplier`)}
            validateTitle={t(`global.${isArchived ? 'unarchive' : 'archive'}`)}
            handleSuccess={() => {
              enqueueSnackbar(t(`${isArchived ? 'unarchive' : 'archive'}.success.supplier`), {variant: 'success'});
            }}
            handleValidate={supplierId
              ? () => supplierDetailsService.updateSupplierStatus(supplierId, isArchived ? GlobalStatus.ACTIVE : GlobalStatus.ARCHIVED)
              : undefined}
            label={t(`global.${isArchived ? 'unarchive' : 'archive'}`)}/>,
          <OptionWithAlertComponent
            key="delete"
            alertTitle={t('delete.title')}
            alertDescription={t('delete.description.supplier')}
            validateTitle={t('global.delete')}
            handleSuccess={() => {
              enqueueSnackbar(t('delete.success.supplier'), {variant: 'success'});
              handleClose();
            }}
            handleValidate={supplierId ? () => supplierDetailsService.deleteSupplier(supplierId) : undefined}
            label={t('global.delete')}/>
        ]}
        readOnly={isArchived}/>
      {!!openContact && (
        <ManageContactDrawer
          contact={typeof openContact !== 'boolean' ? openContact : undefined}
          readOnly={isArchived}
          handleClose={() => setOpenContact(false)}
          handleAdd={(contact) => {
            if (!supplierId) return;

            if (typeof openContact === 'boolean') {
              supplierDetailsService.createSupplierContact(supplierId, contact).subscribe({
                next: () => {
                  enqueueSnackbar(t('unitDetails.success.contact.create'), {variant: 'success'});
                  setOpenContact(false);
                },
                error: (err) => enqueueSnackbar(err.text, err.options),
              });
            } else {
              if (!contact.id) return;
              supplierDetailsService.updateSupplierContact(supplierId, contact.id, contact).subscribe({
                next: () => {
                  enqueueSnackbar(t('unitDetails.success.contact.edit'), {variant: 'success'});
                },
                error: (err) => enqueueSnackbar(err.text, err.options),
              });
            }
          }}/>
      )}
    </>
  );
};

export default HeadOfficeTab;
